import { differenceInDays, parseISO } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useLoyaltyBrand } from "../../components/loyalty-brand-provider";
import {
  LoyaltyConstantTier,
  LoyaltyDerivedTier,
} from "../../gen/witbooking/backoffice/loyalty/tier/v1/loyalty_tier_api_pb";
import {
  Customer,
  CustomerOrganization,
} from "../../gen/witbooking/loyalty/customer/v1/customer_api_pb";
import { CustomerTransactionReportItem } from "../../gen/witbooking/loyalty/report/v1/report_api_pb";
import { useBackofficeClient } from "../../lib/http";
import { customerMessages, customerTableHeaderMessages } from "../loyaltyCustomer/messages";
import * as loyaltyApi from "../shared/api/loyaltyApi";
import { showErrorAlert } from "../shared/crud/error-messages";
import { dateFilter, selectFilter } from "../shared/crud/filter-widgets";
import CrudList, { TableColumns } from "../shared/crud/list";
import { defaultCrudMessages } from "../shared/crud/simple-crud";
import { promptFileDownloadFromBlob } from "../shared/file";
import * as TableRenderers from "../shared/renderers/table";
import { DateRenderer, mapRenderer, StringRenderer } from "../shared/renderers/table";
import { ORGANIZATION_TYPE } from "../witadmin/Common/constants";
import { ExportAction } from "./exportAction";
import { FilterValues, tableFilters } from "./tableFilters";

const tableHeaderMessages = defineMessages({
  ...customerTableHeaderMessages,
  totalReservationCount: {
    id: "loyaltyCustomerTransactionReport.totalReservationCount",
    defaultMessage: "Reservations booked",
  },
  totalRoomNights: {
    id: "loyaltyCustomerTransactionReport.totalRoomNights",
    defaultMessage: "Room nights booked",
  },
  totalReservationAmount: {
    id: "loyaltyCustomerTransactionReport.totalReservationAmount",
    defaultMessage: "Revenue booked",
  },
  pastReservationCount: {
    id: "loyaltyCustomerTransactionReport.pastReservationCount",
    defaultMessage: "Past reservations",
  },
  pastRoomNights: {
    id: "loyaltyCustomerTransactionReport.pastRoomNights",
    defaultMessage: "Past room nights",
  },
  pastReservationAmount: {
    id: "loyaltyCustomerTransactionReport.pastReservationAmount",
    defaultMessage: "Past revenue",
  },
  organizationType: {
    id: "loyaltyOrganization.organizationType",
    defaultMessage: "Type",
  },
  organizationName: {
    id: "loyaltyCustomerTransactionReport.organizationName",
    defaultMessage: "Org. name",
  },
  tierTicker: {
    id: "loyaltyCustomerTransactionReport.tierTicker",
    defaultMessage: "Level",
  },
});

const messages = {
  ...defaultCrudMessages,
  ...customerMessages,
  tableHeaders: tableHeaderMessages,
  entityName: {
    id: "menu.loyalty.customerTransactionReport",
    defaultMessage: "Customer transaction report",
  },
  exportWithValuesOnly: {
    id: "loyaltyCustomerTransactionReport.exportWithValuesOnly",
    defaultMessage: "Export all",
  },
  tooManyResultsError: {
    id: "loyaltyCustomerTransactionReport.tooManyResultsError",
    defaultMessage: "Too many results",
  },
  createdAtFilterError: {
    id: "loyaltyCustomerTransactionReport.error.createdAtFilter",
    defaultMessage: "El rango de fechas no puede exceder los 31 días.",
  },
  errorTitle: {
    id: "loyaltyCustomerTransactionReport.error.title",
    defaultMessage: "Error",
  },
};

const defaultPageSize = 50;
const pageSizes = [50, 100, 500];

export interface ReportItem
  extends Customer.AsObject,
    Omit<CustomerTransactionReportItem.AsObject, "customer"> {
  active: string;
  organizationType: string;
  organizationName: string;
}

const tableColumns: TableColumns<ReportItem> = {
  name: {
    renderer: TableRenderers.StringRenderer,
    width: 150,
  },
  lastName: {
    renderer: TableRenderers.StringRenderer,
    width: 160,
  },
  emailAddress: {
    renderer: TableRenderers.StringRenderer,
    width: 220,
  },
  tierTicker: {
    renderer: TableRenderers.StringRenderer,
    width: 160,
  },
  organizationType: {
    renderer: TableRenderers.StringRenderer,
    width: 160,
  },
  organizationName: {
    renderer: TableRenderers.StringRenderer,
    width: 160,
  },
  tagsList: {
    renderer: mapRenderer(StringRenderer, v => {
      if (!v || !v.length) {
        return undefined;
      }
      return v.toString();
    }),
    width: 200,
  },
  createdAt: {
    renderer: mapRenderer(DateRenderer, v => v || undefined),
    width: 200,
  },
  totalReservationCount: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
  totalRoomNights: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
  totalReservationAmount: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
  pastReservationCount: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
  pastRoomNights: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
  pastReservationAmount: {
    renderer: TableRenderers.NumberRenderer,
    width: 150,
  },
};

function buildFilterDto(
  filterValues: FilterValues,
  allTiers: (LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject | undefined)[]
): Record<string, string> {
  return (
    filterValues &&
    Object.entries(filterValues).reduce((acc, entry) => {
      const [key, value] = entry;
      if (!value) {
        return acc;
      }
      switch (key) {
        case "createdAt":
          const { startDate, endDate } = filterValues.createdAt!;
          return { ...acc, filterDatesBy: key, fromDate: startDate, toDate: endDate };
        case "transactionDate":
          const {
            startDate: transStartDate,
            endDate: transEndDate,
          } = filterValues.transactionDate!;
          return { ...acc, transactionFromDate: transStartDate, transactionToDate: transEndDate };
        case "tierType":
          const tier = allTiers.find(it =>
            it?.ticker.toLowerCase().includes(filterValues?.tierType!.toLowerCase())
          );
          return { ...acc, tierId: tier?.externalSystemId as string };
        case "active":
          return { ...acc, status: filterValues.active === "false" ? "INACTIVE" : "ACTIVE" };
        default:
          return { ...acc, [key]: value as string };
      }
    }, {})
  );
}

function buildOrganizationInfo(organizationsList: CustomerOrganization.AsObject[]) {
  const firstOrganization: CustomerOrganization.AsObject | undefined =
    organizationsList.length > 0 ? organizationsList[0] : undefined;
  const type = firstOrganization?.organizationType;
  return {
    organizationType:
      (type && (type === 2 ? ORGANIZATION_TYPE.AGENCY : ORGANIZATION_TYPE.COMPANY)) || "INDIVIDUAL",
    organizationName: firstOrganization?.ticker || "",
  };
}

function dtoToReportItem(cTri: CustomerTransactionReportItem.AsObject): ReportItem {
  const { customer, ...rest } = cTri;
  return {
    ...customer!,
    ...rest,
    active: customer!.status.toString(),
    ...buildOrganizationInfo(customer!.organizationsList),
  };
}

function isValidDateRange(startDate?: Date, endDate?: Date): boolean {
  if (startDate && endDate) {
    const diffInDays = differenceInDays(
      parseISO(endDate.toString()),
      parseISO(startDate.toString())
    );
    return diffInDays <= 31;
  }
  return true;
}

async function validateDateRanges(filterValues: FilterValues) {
  if (filterValues?.createdAt) {
    const isValidCreatedAtRange = isValidDateRange(
      filterValues.createdAt.startDate,
      filterValues.createdAt.endDate
    );

    if (!isValidCreatedAtRange) {
      return false;
    }
  }
  if (filterValues?.transactionDate) {
    const isValidTransactionDateRange = isValidDateRange(
      filterValues.transactionDate.startDate,
      filterValues.transactionDate.endDate
    );

    if (!isValidTransactionDateRange) {
      return false;
    }
  }
  return true;
}

function LoyaltyCustomerTransactionReport() {
  const brand = useLoyaltyBrand()?.ticker || "";
  const brandId = useLoyaltyBrand()?.externalSystemId || "";
  const [allTiers, setAllTiers] = useState<
    (LoyaltyConstantTier.AsObject | LoyaltyDerivedTier.AsObject | undefined)[] | undefined
  >();
  const [rowCount, setRowCount] = useState<number | undefined>();
  const [filterValuesCopy, setFilterValuesCopy] = useState<FilterValues>({} as FilterValues);
  const [forceUpdate, setForceUpdate] = useState(0);
  const intl = useIntl();
  const backofficeClient = useBackofficeClient();

  useEffect(() => {
    tableFilters.active = {
      filterFn: "async",
      widget: selectFilter(intl, messages?.activeQuestion),
    };
    tableFilters.createdAt = {
      filterFn: "async",
      widget: dateFilter("", intl),
    };
    tableFilters.transactionDate = {
      ...tableFilters.transactionDate,
      filterFn: "async",
      widget: dateFilter("transactionDate", intl),
    };
  }, [intl]);

  useEffect(() => {
    const asyncFun = async () => {
      if (!allTiers && brand) {
        const res = await loyaltyApi.getLoyaltyTiersByBrand(brand);
        setAllTiers(res.tiersList.map(it => (it.constantTier ? it.constantTier : it.derivedTier)));
        setForceUpdate(forceUpdate + 1);
      }
    };
    asyncFun().then(() => undefined);
  }, [allTiers, brand, forceUpdate]);

  const onGetAll = useCallback(
    async ({
      filterValues,
      currentPage,
      pageSize,
      setTotalRowsCount,
    }: {
      filterValues: FilterValues | undefined;
      currentPage: number;
      pageSize: number;
      setTotalRowsCount: any;
    }) => {
      if (!brandId || !allTiers) return;

      if (filterValues?.createdAt || filterValues?.transactionDate) {
        const isValid = await validateDateRanges(filterValues);
        if (!isValid) {
          await showErrorAlert({
            title: intl.formatMessage(messages.errorTitle),
            text: intl.formatMessage(messages.createdAtFilterError),
            intl,
          });
          return;
        }
      }

      const filters = filterValues ? buildFilterDto(filterValues, allTiers) : {};
      const response = await loyaltyApi.getCustomerTransactionReport({
        brandId,
        currentPage,
        pageSize,
        filters,
      });

      setTotalRowsCount(response.totalElements);
      setRowCount(response.totalElements);
      if (filterValues) {
        setFilterValuesCopy(filterValues);
      }

      return response.itemsList.map(dtoToReportItem);
    },
    [brandId, allTiers, intl]
  );

  const handleExport = useCallback(
    async ({ filterValues }: { filterValues: FilterValues }) => {
      if (!allTiers || rowCount === undefined) {
        return;
      }
      if (rowCount > 16000) {
        await showErrorAlert({
          title: intl.formatMessage(messages.loadError),
          text: intl.formatMessage(messages.tooManyResultsError),
          intl,
        });
        return;
      }
      try {
        const file = await loyaltyApi.customerTransactionReportExport(backofficeClient, {
          brandTicker: brand,
          brandId,
          filters: buildFilterDto(filterValues, allTiers),
        });
        promptFileDownloadFromBlob(file, "report.xlsx");
      } catch (e) {
        await showErrorAlert({
          title: intl.formatMessage(messages.loadError),
          text: intl.formatMessage(messages.error),
          intl,
        });
        return;
      }
    },
    [allTiers, backofficeClient, brand, brandId, intl, rowCount]
  );

  const disabledExport = useMemo(() => !!(rowCount && rowCount > 50000), [rowCount]);
  const actions = useCallback(() => {
    if (!allTiers) {
      return;
    }
    const filters = buildFilterDto(filterValuesCopy, allTiers);
    return (
      <ExportAction
        brandTicker={brand}
        brandId={brandId}
        intl={intl}
        filters={filters}
        disabledExport={disabledExport}
      />
    );
  }, [allTiers, brand, brandId, filterValuesCopy, intl, disabledExport]);

  return (
    <>
      {brand && (
        <CrudList<ReportItem, FilterValues>
          actions={actions}
          onExport={handleExport}
          messages={messages}
          filters={tableFilters}
          columns={tableColumns}
          onGetAll={onGetAll}
          showSuccessMessage={false}
          paging={true}
          pageSizes={pageSizes}
          defaultPageSize={defaultPageSize}
          initialHiddenColumnNames={[]}
          managerName={"customerTransactionReport"}
          forceLoad={forceUpdate}
        />
      )}
    </>
  );
}

export default withAuthorizationVerifier(LoyaltyCustomerTransactionReport);
